import { useEffect, useRef, useState } from 'react';
import { isStringPreference, useUserPreference } from '../lib/user-preferences.js';

export type SortOrder = 'asc' | 'desc';

/**
 * Sortable table header cell. Clicking toggles asc/desc when the column is
 * already active, otherwise switches to it (ascending first).
 */
export function SortableTh({ label, sortKey, sortBy, sortOrder, onSort, className }: {
  label: string;
  sortKey: string;
  sortBy: string;
  sortOrder: SortOrder;
  onSort: (key: string) => void;
  className?: string;
}) {
  const active = sortBy === sortKey;
  return (
    <th className={`text-left px-4 py-3 font-medium ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-white ${active ? 'text-white' : ''}`}
      >
        {label}
        {active && <span aria-hidden className="text-[10px]">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

/**
 * Cursor pagination footer: "Showing X–Y of N" plus Previous/Next.
 */
export function TablePager({ pageIndex, count, limit, total, hasNext, onPrev, onNext }: {
  pageIndex: number;
  /** Rows on the current page. */
  count: number;
  limit: number;
  total: number | undefined;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const from = count === 0 ? 0 : pageIndex * limit + 1;
  const to = pageIndex * limit + count;
  const btn = 'px-3 py-1 rounded-lg border border-border text-sm text-muted hover:text-white hover:border-accent disabled:opacity-40 disabled:hover:text-muted disabled:hover:border-border';
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
      <span className="text-sm text-muted">
        Showing {from}–{to}{total != null ? ` of ${total}` : ''}
      </span>
      <div className="flex gap-2">
        <button type="button" onClick={onPrev} disabled={pageIndex === 0} className={btn}>Previous</button>
        <button type="button" onClick={onNext} disabled={!hasNext} className={btn}>Next</button>
      </div>
    </div>
  );
}

/**
 * Shared sort + cursor-pagination state for list tables.
 *
 * Sort is remembered per user and table. Filter changes still restore the
 * default sort and first page; the initial render keeps the saved sort.
 */
export function useTableControls(defaultSortBy: string, defaultSortOrder: SortOrder, resetDeps: unknown[], preferenceKey = window.location.pathname) {
  const [sortBy, setSortBy] = useUserPreference(`${preferenceKey}.sortBy`, defaultSortBy, { urlKeys: ['sortBy'], validate: isStringPreference });
  const [sortOrder, setSortOrder] = useUserPreference<SortOrder>(`${preferenceKey}.sortOrder`, defaultSortOrder, {
    urlKeys: ['sortOrder'],
    validate: (value) => value === 'asc' || value === 'desc',
  });
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const previousResetDeps = useRef(resetDeps);
  const pageIndex = cursors.length - 1;
  const cursor = cursors[pageIndex] ?? null;

  useEffect(() => {
    const changed = previousResetDeps.current.length !== resetDeps.length
      || previousResetDeps.current.some((value, index) => !Object.is(value, resetDeps[index]));
    previousResetDeps.current = resetDeps;
    if (changed) {
      setSortBy(defaultSortBy);
      setSortOrder(defaultSortOrder);
    }
    setCursors([null]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDeps);

  function toggleSort(key: string) {
    setCursors([null]);
    if (key === sortBy) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  }

  function goNext(nextCursor: string | null | undefined) {
    if (!nextCursor) return;
    setCursors((c) => [...c, nextCursor]);
  }

  function goPrev() {
    setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c));
  }

  return { sortBy, sortOrder, cursor, pageIndex, toggleSort, goNext, goPrev };
}
